import React from "react";
import { Box, Typography, Button } from "@mui/material";

// Routes are code-split, so a route's chunk can fail to load in three ways:
//   1. offline, and that chunk was never cached by the service worker
//   2. right after a deploy, when an open tab asks for a chunk URL that no
//      longer exists
//   3. the service worker cached a bad response for a chunk URL, so the tab is
//      stuck on it across deploys
// Without a boundary any of these renders a blank white page.
//
// Auto-retry is strictly bounded. navigator.onLine is true whenever the network
// interface is up, even when nothing is actually reachable, so an unbounded
// retry produces a phone that reloads itself every few seconds forever. Two
// attempts per tab session, each escalating what it clears, then we stop and
// show the UI. The counter is never reset automatically.
const RETRY_COUNT_KEY = "routeChunkRetryCount";
const RETRY_AT_KEY = "routeChunkRetryAt";
const RETRY_COOLDOWN_MS = 15000;
const MAX_RETRIES = 2;

function readSession(key) {
  try {
    return Number(sessionStorage.getItem(key)) || 0;
  } catch (e) {
    // sessionStorage throws in some private-mode configurations. Treat that as
    // "cannot track retries", which means we must not retry at all.
    return null;
  }
}

function writeSession(key, value) {
  try {
    sessionStorage.setItem(key, String(value));
    return true;
  } catch (e) {
    return false;
  }
}

// Attempt 1 drops the runtime asset cache and asks the worker to look for a new
// build. Attempt 2 removes every cache and the worker itself, which is the only
// thing that can rescue a device whose precached shell is the stale part.
async function clearStaleCaches({ full }) {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      const doomed = full
        ? keys
        : keys.filter((key) => key.startsWith("static-assets"));
      await Promise.all(doomed.map((key) => caches.delete(key)));
    }
  } catch (e) {
    // Storage can be unavailable; a plain reload is still worth trying.
  }

  try {
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        if (full) {
          await registration.unregister();
        } else {
          await registration.update();
        }
      }
    }
  } catch (e) {
    // Same.
  }
}

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, recovering: false };
    this.handleManualRetry = this.handleManualRetry.bind(this);
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    const isChunkError =
      error?.name === "ChunkLoadError" ||
      /Loading chunk|dynamically imported module|Unexpected token '<'/i.test(
        error?.message ?? ""
      );

    if (!isChunkError || !navigator.onLine) return;

    const attempts = readSession(RETRY_COUNT_KEY);
    const lastRetryAt = readSession(RETRY_AT_KEY);
    if (attempts === null || lastRetryAt === null) return;
    if (attempts >= MAX_RETRIES) return;

    const now = Date.now();
    if (now - lastRetryAt < RETRY_COOLDOWN_MS) return;

    if (!writeSession(RETRY_COUNT_KEY, attempts + 1)) return;
    if (!writeSession(RETRY_AT_KEY, now)) return;

    this.setState({ recovering: true });
    clearStaleCaches({ full: attempts + 1 >= MAX_RETRIES }).then(() => {
      window.location.reload();
    });
  }

  // The manual button is a deliberate user action, so it gets the full reset
  // and a fresh retry budget.
  handleManualRetry() {
    this.setState({ recovering: true });
    writeSession(RETRY_COUNT_KEY, 0);
    writeSession(RETRY_AT_KEY, 0);
    clearStaleCaches({ full: true }).then(() => {
      window.location.reload();
    });
  }

  render() {
    if (!this.state.failed) return this.props.children;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          minHeight: "60vh",
          px: 3,
          textAlign: "center",
          color: "#fff",
        }}
      >
        <Typography variant="h6">
          {this.state.recovering ? "Reloading…" : "This page didn’t load"}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
          {navigator.onLine
            ? "Something went wrong loading this section. Please try again."
            : "You appear to be offline. Reconnect and try again."}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            disabled={this.state.recovering}
            onClick={this.handleManualRetry}
            sx={{ background: "linear-gradient(-45deg, #44A08D, #093637)" }}
          >
            Try again
          </Button>
          <Button
            variant="outlined"
            disabled={this.state.recovering}
            onClick={() => {
              window.location.href = "/";
            }}
            sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}
          >
            Go home
          </Button>
        </Box>
      </Box>
    );
  }
}

export default RouteErrorBoundary;
